import type { SortByOption, SortingDirection } from '@awork/_shared/models/sort-by-option.model'
import type {
  IDocumentSettings,
  IEntityTimesSettings,
  IProjectTaskListSetting,
  IProjectTaskListSettings,
  IProjectDocsSetting,
  IProjectTimeTrackingTrackedPlannedSetting,
  IProjectViewSetting,
  ITaskViewListsSetting,
  IViewPersistenceSetting
} from '@awork/framework/models/view-setting.model'
import type { TaskFilterSidebarSettings } from '@awork/features/task/types/task-filter-sidebar-settings'
import type { ProjectPageSettings } from '@awork/features/project/types/project-page-settings'
import type { UserPageSettings } from '@awork/features/user/state/types'
import type { CompanyPageSettings } from '@awork/features/company/types/company-page-settings'
import type { IOnboardingSettings } from '@awork/features/onboarding/models/onboarding-settings.model'
import type { PlannerCalendarControlsSettings } from '@awork/_shared/models/planner-calendar-controls.settings.model'
import type { TransientEvents } from '@awork/_shared/models/transient-events.model'
import type { EntityName } from '@awork/_shared/services/user-settings-service/user-settings.service'
import type { GroupByOptions } from '@awork/_shared/models/group-by-options.model'
import type { IDesktopAppUserSetting } from '@awork/_shared/models/user-setting.model'
import type { ReasoningLevel } from '@awork/features/agent/models/agent-configuration.model'
import type { AgentLibrarySettings } from '@awork/features/agent/types/agent-library-settings'
import type { AiThreadLocalFolder } from '@awork/features/agent/services/agent-service/types'

/**
 * Persisted column customization settings for user grid
 * Uses column ID as key to ensure settings survive column additions/removals
 */
export interface GridColumnSettings<C extends string> {
  id: C
  width?: number
  order?: number
  visible?: boolean
}

/**
 * Persisted grid user settings
 */
export interface GridUserSettings<C extends string> {
  sortBy?: SortByOption<C>
  gridColumnSettings?: GridColumnSettings<C>[]
}

export enum SortByOptions {
  taskName = 'taskName',
  project = 'project',
  createdOn = 'createdOn',
  startOn = 'startOn',
  dueOn = 'dueOn',
  typeOfWork = 'typeOfWork',
  effort = 'effort',
  taskStatus = 'taskStatus',
  priority = 'priority',
  taskIdentifier = 'taskIdentifier',
  taskStatusType = 'taskStatusType',
  createdBy = 'createdBy',
  client = 'client',
  user = 'user',
  default = 'default'
}

export interface IGroupingSorting {
  groupBy?: GroupByOptions
  sortBy?: SortByOptions
  orderBy?: SortingDirection
}

export interface NotificationUserSetting {
  event: TransientEvents
  shownOn: Date
}

export type MaintenanceCommandType = 'wipeStore'

export interface MaintenanceCommand {
  command: MaintenanceCommandType
  timestamp: Date
  args?: string[]
}

export interface AgentChatSettings {
  defaultAgent?: string | null
  detailsPanelExpanded?: boolean
  sidebarExpanded?: boolean
  sidebarWidth?: number
  collapsedThreadGroupIds?: string[]
  threadListToggledKeys?: string[]
  threadListFavouritesExpanded?: boolean
  modelSelection?: AgentChatModelSelection
  toolAccess?: AgentChatToolAccess
  localFolders?: AiThreadLocalFolder[]
}

export interface AgentChatModelSelection {
  modelProvider: string
  modelName: string
  reasoningLevel: ReasoningLevel
  advanced?: boolean
}

export interface AgentChatToolAccess {
  awork: boolean
  webSearch: boolean
  googleGenAi: boolean
  openAiImageGeneration: boolean
}

/**
 * Persisted view state of the AI Usage & activity page.
 * Column, sort, and group ids are stored as plain strings because they are validated on read.
 */
export interface AgentUsageActivitySettings extends GridUserSettings<string> {
  dateRangeMode?: string
  groupBy?: string
}

export type EntitySelectorEntityType =
  'user' | 'company' | 'project' | 'task' | 'team' | 'agent' | 'document' | 'workType' | 'prompt'

export type EntitySelectorSource = 'recent' | 'favorites' | 'my' | 'context' | 'all'

export interface RecentEntityReference {
  id: string
  selectedAt: number
}

export interface EntitySelectorSourceChoice {
  source: EntitySelectorSource
  selectedAt: number
}

export interface EntitySelectorSettings {
  recentByType?: Partial<Record<EntitySelectorEntityType, RecentEntityReference[]>>
  lastSourceByType?: Partial<Record<EntitySelectorEntityType, EntitySelectorSourceChoice>>
}

export interface EntitySettings {
  entityName: EntityName
  entityId: string
  [key: string]: unknown
}

// Utility type that filters SettingsState keys to only those whose values are arrays of EntitySettings
export type EntitySettingsKey<K> = K extends keyof SettingsState
  ? SettingsState[K] extends EntitySettings[]
    ? K
    : never
  : never
export type EntitySettingsValue<K> = K extends keyof SettingsState
  ? SettingsState[K] extends (infer U extends EntitySettings)[]
    ? U
    : never
  : never

export interface SettingsState {
  notifications: NotificationUserSetting[]
  viewSettings: IViewPersistenceSetting
  projectTaskListSettings: IProjectTaskListSetting[]
  projectTaskList: IProjectTaskListSettings[]
  projectTimeTrackingTrackedPlannedSettings: IProjectTimeTrackingTrackedPlannedSetting[]
  taskViewListsSettings: ITaskViewListsSetting[]
  taskListWidgetSettings: ITaskViewListsSetting[]
  taskFilterSidebarSettings: TaskFilterSidebarSettings
  projectSettings: IProjectViewSetting[]
  entityTimesSettings: IEntityTimesSettings[]
  hideTimers: boolean
  sortingGrouping: IGroupingSorting
  documents: IDocumentSettings
  projectPage: ProjectPageSettings
  aworkAIProjectPage: ProjectPageSettings
  userPage: UserPageSettings
  aworkAIUserPage: UserPageSettings
  aworkAIProjectDocs: Partial<IProjectDocsSetting>
  companyPage: CompanyPageSettings
  onboarding: Partial<IOnboardingSettings>
  plannerCalendarControlsSettings: PlannerCalendarControlsSettings
  desktopApp: IDesktopAppUserSetting
  commands: MaintenanceCommand[]
  agentChat: AgentChatSettings
  agentUsageActivity: AgentUsageActivitySettings
  agentLibrary: AgentLibrarySettings
  entitySelectors: EntitySelectorSettings
}
